import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { ToastContext } from '../../context/ToastContext';
import { fetchOrders } from '../../utils/api';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import {
  getOrderStatusLabel,
  getOrderStatusBadgeClass,
  ORDER_STATUS,
  ORDER_TIMELINE,
} from '../../utils/constants';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import ErrorState from '../../components/common/ErrorState';
import { ListSkeleton } from '../../components/common/Skeleton';
import {
  ShoppingBag,
  ChevronDown,
  CheckCircle2,
  Circle,
  MapPin,
  MessageSquareText,
} from 'lucide-react';

const STATUS_FILTERS = [
  { value: 'all', label: '전체' },
  { value: 'preparing', label: '준비중' },
  { value: 'shipping', label: '배송중' },
  { value: 'delivered', label: '배송완료' },
  { value: 'cancelled', label: '취소' },
];

const MyOrdersPage = () => {
  const { user } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setIsError(false);
      const data = await fetchOrders();
      const userOrders = data.filter((order) => order.userId === user?.id);
      setOrders(userOrders);
    } catch (error) {
      setIsError(true);
      showToast('주문 내역을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [user, showToast]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return orders;
    return orders.filter((o) => o.orderStatus === statusFilter);
  }, [orders, statusFilter]);

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    await new Promise((resolve) => setTimeout(resolve, 400)); // mock
    setOrders((prev) =>
      prev.map((o) =>
        o.id === cancelTarget ? { ...o, orderStatus: 'cancelled' } : o
      )
    );
    setCancelling(false);
    setCancelTarget(null);
    showToast('주문이 취소되었습니다.', 'success');
  };

  // 배송 상태 타임라인 (ORDER_TIMELINE 기준)
  const renderTimeline = (order) => {
    if (order.orderStatus === 'cancelled') {
      return (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          이 주문은 취소되었습니다.
        </div>
      );
    }
    const currentStep = ORDER_STATUS[order.orderStatus]?.step ?? 0;
    return (
      <ol className="flex items-center justify-between">
        {ORDER_TIMELINE.map((step, idx) => {
          const done = idx <= currentStep;
          return (
            <li
              key={step}
              className="flex flex-1 flex-col items-center text-center"
            >
              <div className="flex w-full items-center">
                {idx > 0 && (
                  <span
                    className={`h-0.5 flex-1 ${
                      idx <= currentStep ? 'bg-primary-500' : 'bg-accent-200'
                    }`}
                  />
                )}
                {done ? (
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-primary-600" />
                ) : (
                  <Circle className="h-5 w-5 flex-shrink-0 text-accent-300" />
                )}
                {idx < ORDER_TIMELINE.length - 1 && (
                  <span
                    className={`h-0.5 flex-1 ${
                      idx < currentStep ? 'bg-primary-500' : 'bg-accent-200'
                    }`}
                  />
                )}
              </div>
              <span
                className={`mt-1 text-xs ${
                  done ? 'font-medium text-primary' : 'text-accent-500'
                }`}
              >
                {getOrderStatusLabel(step)}
              </span>
            </li>
          );
        })}
      </ol>
    );
  };

  if (loading) {
    return (
      <Card>
        <div className="p-6">
          <h2 className="mb-6 text-xl font-bold text-primary">주문 내역</h2>
          <ListSkeleton rows={3} />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div className="p-6">
          <ErrorState
            title="주문 내역을 불러오지 못했습니다"
            message="네트워크 상태를 확인한 후 다시 시도해주세요."
            onRetry={loadOrders}
          />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="p-6">
        <h2 className="text-xl font-bold text-primary mb-6">주문 내역</h2>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="w-16 h-16 mx-auto text-accent-300 mb-4" />
            <p className="text-accent-600 mb-4">주문 내역이 없습니다.</p>
            <Button variant="outline" onClick={() => navigate('/wreaths')}>
              화환 둘러보기
            </Button>
          </div>
        ) : (
          <>
            {/* 상태 필터 */}
            <div className="mb-4 flex flex-wrap gap-2">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setStatusFilter(f.value)}
                  aria-pressed={statusFilter === f.value}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                    statusFilter === f.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-accent-100 text-accent-700 hover:bg-accent-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <p className="rounded-lg bg-accent-50 px-4 py-10 text-center text-sm text-accent-600">
                해당 상태의 주문이 없습니다.
              </p>
            ) : (
              <div className="space-y-6">
                {filtered.map((order) => {
                  const expanded = expandedId === order.id;
                  return (
                    <div
                      key={order.id}
                      className="border border-accent-200 rounded-lg p-4 hover:border-primary transition-colors"
                    >
                      {/* 카드 헤더 (클릭 시 상세 확장) */}
                      <button
                        onClick={() =>
                          setExpandedId(expanded ? null : order.id)
                        }
                        aria-expanded={expanded}
                        className="flex w-full items-start justify-between text-left"
                      >
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-accent-600">
                              주문번호: {order.id}
                            </p>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${getOrderStatusBadgeClass(
                                order.orderStatus
                              )}`}
                            >
                              {getOrderStatusLabel(order.orderStatus)}
                            </span>
                          </div>
                          <p className="text-xs text-accent-500">
                            주문일시: {formatDateTime(order.orderedAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-lg font-bold text-primary">
                            {formatCurrency(order.totalAmount)}
                          </p>
                          <ChevronDown
                            className={`h-5 w-5 text-accent-400 transition-transform ${
                              expanded ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </button>

                      {/* 상품 목록 */}
                      <div className="space-y-2 mt-4 mb-4">
                        {order.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between"
                          >
                            <p className="font-medium text-primary">
                              {item.wreathName}
                            </p>
                            <p className="text-sm text-accent-600">
                              {formatCurrency(item.price)} x {item.quantity}개
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* 배송 상태 타임라인 */}
                      <div className="pt-3 border-t border-accent-100">
                        {renderTimeline(order)}
                      </div>

                      {/* 확장 상세 */}
                      {expanded && (
                        <div className="mt-4 space-y-3 border-t border-accent-100 pt-4 text-sm">
                          <div className="flex items-start gap-2 text-accent-700">
                            <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-500" />
                            <div>
                              <p className="font-medium text-primary">
                                배송지
                              </p>
                              <p>{order.deliveryAddress}</p>
                              <p>{order.deliveryPhone}</p>
                            </div>
                          </div>
                          {order.messageCard && (
                            <div className="flex items-start gap-2 text-accent-700">
                              <MessageSquareText className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-500" />
                              <div>
                                <p className="font-medium text-primary">
                                  근조 리본 / 메시지 카드
                                </p>
                                <p className="italic">"{order.messageCard}"</p>
                                {order.senderName && (
                                  <p className="text-xs text-accent-500">
                                    보내는 분: {order.senderName}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          {order.deliveredAt && (
                            <p className="text-xs text-accent-500">
                              배송 완료: {formatDateTime(order.deliveredAt)}
                            </p>
                          )}

                          {/* 액션 */}
                          <div className="flex flex-wrap gap-2 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate('/wreaths')}
                            >
                              재주문
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                showToast(
                                  '영수증이 발급되었습니다. (데모)',
                                  'success'
                                )
                              }
                            >
                              영수증
                            </Button>
                            {(order.orderStatus === 'pending' ||
                              order.orderStatus === 'preparing') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCancelTarget(order.id)}
                                className="text-red-600 hover:bg-red-50 border-red-300"
                              >
                                주문 취소
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* 주문 취소 확인 모달 */}
      <ConfirmModal
        isOpen={cancelTarget != null}
        onClose={() => !cancelling && setCancelTarget(null)}
        onConfirm={confirmCancel}
        title="주문 취소"
        message="이 주문을 취소하시겠습니까? 취소 후에는 되돌릴 수 없습니다."
        confirmLabel="주문 취소"
        confirmVariant="danger"
        loading={cancelling}
      />
    </Card>
  );
};

export default MyOrdersPage;
